import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, Zap, Sparkles, Music, Flame } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const RITUALS = [
  {
    id: 'night',
    icon: Moon,
    title: 'The Night Ritual',
    subtitle: 'For Secret Nº1, Nº5, Nº9',
    setting: 'Low light. Quiet space. No screens.',
    steps: [
      'Dim the lights to warm tones only',
      'Put your phone in another room',
      'Light something — a candle, incense, whatever grounds you',
      'Prepare your Secret slowly. No rush.',
      'Sit somewhere comfortable. Close your eyes for the first minute.',
      'Let the experience arrive on its own terms',
    ],
    pairing: 'Ambient music, herbal tea, a weighted blanket',
    mood: 'Stillness. Surrender. Sleep.',
  },
  {
    id: 'focus',
    icon: Zap,
    title: 'The Focus Ritual',
    subtitle: 'For Secret Nº2, Nº6, Nº10',
    setting: 'Clean desk. Natural light. One task.',
    steps: [
      'Clear your workspace of everything non-essential',
      'Open the window if possible — fresh air sharpens',
      'Set a single intention for the session',
      'Prepare your Secret with care',
      'Start working within five minutes — let the clarity meet you mid-task',
      'Avoid switching tasks for at least 45 minutes',
    ],
    pairing: 'Lo-fi beats, black coffee, a clean notebook',
    mood: 'Presence. Precision. Flow.',
  },
  {
    id: 'calm',
    icon: Sun,
    title: 'The Calm Ritual',
    subtitle: 'For Secret Nº3, Nº7',
    setting: 'Comfortable space. Soft textures. Golden hour.',
    steps: [
      'Change into something comfortable',
      'Find a space where you won\'t be interrupted',
      'Play something gentle — or nothing at all',
      'Prepare your Secret as an act of care, not habit',
      'Sit with it. Let the tension in your body unwind.',
      'No agenda. Just being.',
    ],
    pairing: 'Warm lighting, acoustic music, a good blanket',
    mood: 'Release. Warmth. Ease.',
  },
  {
    id: 'signature',
    icon: Sparkles,
    title: 'The Signature Ritual',
    subtitle: 'For Secret Nº4, Nº8',
    setting: 'Your favorite space. Set the scene.',
    steps: [
      'This is your time — treat it like an event',
      'Curate the atmosphere: music, scent, lighting',
      'Dress the moment — even if you\'re alone',
      'Prepare your Secret with intention',
      'Notice the layers as they unfold',
      'Stay curious. Each experience reveals something new.',
    ],
    pairing: 'Vinyl records, dark chocolate, your favorite chair',
    mood: 'Discovery. Depth. Indulgence.',
  },
];

const RitualGuidePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Experience Design
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="ritual-heading"
          >
            The Ritual Guide
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            A Secret isn't just consumed — it's experienced. Here's how to get the most from each one.
          </motion.p>
        </div>
      </section>

      {/* Rituals */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto space-y-16">
          {RITUALS.map((ritual, index) => {
            const Icon = ritual.icon;
            return (
              <motion.div
                key={ritual.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="border border-white/5 p-8 md:p-12"
                data-testid={`ritual-${ritual.id}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <Icon className="w-5 h-5 text-zinc-500" />
                  <div>
                    <h3 className="font-serif text-2xl text-zinc-50">{ritual.title}</h3>
                    <p className="font-sans text-xs text-zinc-600">{ritual.subtitle}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                  {/* Setting */}
                  <div>
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-3">Setting</p>
                    <p className="font-sans text-sm text-zinc-400 leading-relaxed">{ritual.setting}</p>
                  </div>

                  {/* Steps */}
                  <div className="md:col-span-2">
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-3">The Steps</p>
                    <ol className="space-y-2">
                      {ritual.steps.map((step, i) => (
                        <li key={i} className="font-sans text-sm text-zinc-400 leading-relaxed flex gap-3">
                          <span className="text-zinc-600 font-mono text-xs mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 pt-8 border-t border-white/5">
                  <div>
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-2">Pairs With</p>
                    <p className="font-sans text-sm text-zinc-400 italic">{ritual.pairing}</p>
                  </div>
                  <div>
                    <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-2">The Mood</p>
                    <p className="font-serif text-lg text-zinc-300">{ritual.mood}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-sans text-sm text-zinc-500 mb-6">Ready to begin your ritual?</p>
            <button
              data-testid="ritual-cta"
              onClick={() => navigate('/collection')}
              className="bg-white text-black px-10 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
            >
              Find Your Secret
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default RitualGuidePage;
